/**
 * Collapse concurrent calls onto one in-flight request, per session.
 *
 * Extracted from `session-store.ts` because the six lines it replaces had two
 * defects in two review passes, and neither was reachable by a test that reads
 * the source. Both were about the SLOT rather than the request:
 *
 *   * A slot outlived a session. A sign-out mid-request left the promise in
 *     place, the next session joined it, and that promise then wrote nothing —
 *     its own epoch guard had already made its results unwelcome. The new
 *     session never issued a read of its own and its screen sat on a spinner.
 *   * A late settle cleared someone else's slot. The old request's `finally`
 *     nulled the slot unconditionally, so if a new session had already filled
 *     it, the next caller issued a duplicate instead of joining the read
 *     that was actually running.
 *
 * Both are one-line mistakes that look correct in review, which is the argument
 * for a tested primitive rather than a fourth careful inline copy.
 *
 * The epoch is passed IN rather than read from a module the helper imports:
 * that keeps this a pure function of its arguments, so the cross-session cases
 * can be driven directly in a test instead of by faking a sign-out.
 */
export function createSessionSingleFlight<T>(): (
  epoch: number,
  run: () => Promise<T>,
) => Promise<T> {
  let slot: { epoch: number; promise: Promise<T> } | null = null;

  return (epoch, run) => {
    // Only this session's work is joinable. A slot left by a previous session
    // is ignored, never awaited — its result is already unwelcome, so joining
    // it would mean waiting for an answer that will be thrown away.
    if (slot && slot.epoch === epoch) return slot.promise;

    const mine = run();
    slot = { epoch, promise: mine };
    return mine.finally(() => {
      // Only if the slot still holds THIS request. A request that outlived a
      // reset must not clear the slot a newer one has since filled.
      if (slot?.promise === mine) slot = null;
    });
  };
}
